import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import {
    FiDownload, FiUsers, FiBriefcase, FiCreditCard,
    FiFileText, FiClipboard, FiDollarSign, FiBookOpen,
    FiAward, FiPackage, FiChevronRight,
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import { academicYearService, financialYearService } from "../../services/yearService";
import studentService from "../../services/studentService";
import employeeService from "../../services/employeeService";
import { transformRow } from "../../utils/excelTransform";
import api from "../../services/api";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const exportToExcel = (rows, columns, filename) => {
    const safeRows = Array.isArray(rows) ? rows : [];
    const safeColumns = Array.isArray(columns) ? columns : [];
    const headers = safeColumns.map((c) => c.header);
    const data = safeRows.map((row) => safeColumns.map((c) => row[c.key] ?? ""));
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);

    ws["!cols"] = safeColumns.map(() => ({ wch: 20 }));

    safeColumns.forEach((_, ci) => {
        const addr = XLSX.utils.encode_cell({ r: 0, c: ci });
        if (ws[addr]) ws[addr].s = { font: { bold: true } };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, `${filename}.xlsx`);
};

const getRows = (payload) => {
    const data = payload?.data ?? payload;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    return [];
};

const getReportPayload = (payload) => payload?.data ?? payload ?? {};

// ─────────────────────────────────────────────────────────────────────────────
// Employee Excel import helpers
// ─────────────────────────────────────────────────────────────────────────────

const cleanExcelValue = (value) => {
    if (value === null || value === undefined) return "";
    return String(value).trim();
};

const excelDateToISO = (value) => {
    if (!value) return "";

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const y = value.getFullYear();
        const m = String(value.getMonth() + 1).padStart(2, "0");
        const d = String(value.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    }

    const text = cleanExcelValue(value);
    if (!text) return "";

    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;

    const match = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (match) {
        const [, d, m, y] = match;
        return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    }

    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) {
        return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
    }

    return text;
};

const getEmployeeCategoryFromDesignation = (designation, explicitCategory = "") => {
    const explicit = cleanExcelValue(explicitCategory);

    if (explicit === "Teaching" || explicit === "Non Teaching") {
        return explicit;
    }

    const d = cleanExcelValue(designation).toLowerCase();

    if (d.includes("laboratory technician") || d.includes("lab technician")) {
        return "Non Teaching";
    }

    if (
        d.includes("professor") ||
        d.includes("lecturer") ||
        d.includes("h.o.d") ||
        d.includes("head of institute") ||
        d.includes("principal")
    ) {
        return "Teaching";
    }

    return "";
};

const transformEmployeeExcelRow = (row) => {
    const fullName = cleanExcelValue(
        row.fullName ?? row["Full Name"] ?? row["Faculty"]
    );
    const employeeCode = cleanExcelValue(
        row.employeeCode ?? row["Employee Code"] ?? row["Pharmacy Council No."]
    );
    const department = cleanExcelValue(
        row.department ?? row["Department"]
    );
    const designation = cleanExcelValue(
        row.designation ?? row["Designation"]
    );
    const qualification = cleanExcelValue(
        row.qualification ?? row["Qualification"]
    );
    const experience = cleanExcelValue(
        row.experience ?? row["Experience"] ?? row["Overall Experience"]
    );
    const mobile = cleanExcelValue(
        row.mobile ?? row["Mobile"] ?? row["Mobile No."]
    );
    const email = cleanExcelValue(
        row.email ?? row["Email"]
    );
    const employeeCategory = getEmployeeCategoryFromDesignation(
        designation,
        row.employeeCategory ?? row["Category"]
    );

    return {
        fullName,
        employeeCode,
        employeeCategory,
        department,
        designation,
        qualification,
        experience,
        gender: cleanExcelValue(row.gender ?? row["Gender"]),
        status: cleanExcelValue(row.status ?? row["Status"]) || "Active",
        mobile,
        email,
        joiningDate: excelDateToISO(row.joiningDate ?? row["Joining Date"]),
        dateOfBirth: excelDateToISO(row.dateOfBirth ?? row["Date of Birth"]),
        address: cleanExcelValue(row.address ?? row["Address"]),
        notes: cleanExcelValue(row.notes ?? row["Notes"]),
    };
};

const MONTHS = ["", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

// ─────────────────────────────────────────────────────────────────────────────
// YearCard
// ─────────────────────────────────────────────────────────────────────────────
const YearCard = ({ year, type, onSetActive, onDelete, isActive }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.75rem 1rem", borderRadius: "8px", border: `1px solid ${isActive ? "#b6d5ec" : "var(--border)"}`, background: isActive ? "#e8f1f8" : "var(--white)", marginBottom: "0.5rem", gap: "0.75rem" }}>
        <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: isActive ? "#002747" : "var(--text)" }}>
                {year.label}
                {isActive && <span style={{ marginLeft: "8px", fontSize: 11, background: "#003b67", color: "#fff", padding: "2px 8px", borderRadius: "99px" }}>Active</span>}
            </div>
            {type === "financial" && year.assessmentYearLabel && (
                <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{year.assessmentYearLabel}</div>
            )}
            <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: "2px" }}>
                {new Date(year.startDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                {" → "}
                {new Date(year.endDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
            </div>
        </div>
        <div style={{ display: "flex", gap: "0.4rem", flexShrink: 0 }}>
            {!isActive && <button className="btn btn-outline btn-sm" onClick={() => onSetActive(year._id)}>Set Active</button>}
            {!isActive && <button className="btn btn-danger  btn-sm" onClick={() => onDelete(year._id, year.label)}>Delete</button>}
        </div>
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Export module card
// ─────────────────────────────────────────────────────────────────────────────
function ExportCard({ icon: Icon, color, title, description, loading, onExport, disabled, disabledReason, children }) {
    return (
        <div style={{
            border: "1px solid var(--border)", borderRadius: 8,
            padding: "1rem 1.1rem",
            borderLeft: `4px solid ${color}`,
            display: "grid", gap: "0.6rem",
            height: "100%",
        }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <div style={{ width: 30, height: 30, borderRadius: 8, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Icon size={14} color={color} />
                    </div>
                    <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{title}</div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{description}</div>
                    </div>
                </div>
                <button
                    className="btn btn-outline btn-sm"
                    onClick={onExport}
                    disabled={loading || disabled}
                    title={disabled ? disabledReason : `Export ${title}`}
                    style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", flexShrink: 0 }}
                >
                    <FiDownload size={12} />
                    {loading ? "Exporting…" : "Export"}
                </button>
            </div>
            {disabled && disabledReason && (
                <div style={{ fontSize: 12, color: "#d97706", display: "flex", alignItems: "center", gap: 4 }}>
                    ⚠ {disabledReason}
                </div>
            )}
            {children}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Settings component
// ─────────────────────────────────────────────────────────────────────────────
export default function Settings() {
    const navigate = useNavigate();
    const { selectAcademicYear, selectFinancialYear, academicYear } = useApp();

    const [academicYears, setAcademicYears] = useState([]);
    const [loadingAY, setLoadingAY] = useState(true);
    const [ayForm, setAyForm] = useState({ label: "", startDate: "", endDate: "", notes: "", setAsActive: true });
    const [savingAY, setSavingAY] = useState(false);

    const [financialYears, setFinancialYears] = useState([]);
    const [loadingFY, setLoadingFY] = useState(true);
    const [fyForm, setFyForm] = useState({ label: "", assessmentYearLabel: "", startDate: "", endDate: "", openingBalance: "", notes: "", setAsActive: true });
    const [savingFY, setSavingFY] = useState(false);

    const [cfSourceId, setCfSourceId] = useState("");
    const [cfTargetId, setCfTargetId] = useState("");
    const [cfPreview, setCfPreview] = useState(null);
    const [loadingCFPreview, setLoadingCFPreview] = useState(false);
    const [executingCF, setExecutingCF] = useState(false);
    const [cfResult, setCfResult] = useState(null);

    const fileInputRef = useRef(null);
    const [importAY, setImportAY] = useState("");
    const [importLoading, setImportLoading] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [importPreviewRows, setImportPreviewRows] = useState(null);
    const [importFile, setImportFile] = useState(null);

    const employeeFileInputRef = useRef(null);
    const [employeeImportJoiningDate, setEmployeeImportJoiningDate] = useState("");
    const [employeeImportDefaultDepartment, setEmployeeImportDefaultDepartment] = useState("");
    const [employeeImportLoading, setEmployeeImportLoading] = useState(false);
    const [employeeImportResult, setEmployeeImportResult] = useState(null);
    const [employeeImportPreviewRows, setEmployeeImportPreviewRows] = useState(null);
    const [employeeImportFile, setEmployeeImportFile] = useState(null);

    const [exportLoading, setExportLoading] = useState({
        students: false,
        employees: false,
        fees: false,
        bonafide: false,
        leaving: false,
        salary: false,
        attendance: false,
        exams: false,
    });
    const [salaryExportMonth, setSalaryExportMonth] = useState(new Date().getMonth() + 1);
    const [salaryExportYear, setSalaryExportYear] = useState(new Date().getFullYear());

    const setExportBusy = (key, val) =>
        setExportLoading((prev) => ({ ...prev, [key]: val }));
    const academicYearRows = Array.isArray(academicYears) ? academicYears : [];
    const financialYearRows = Array.isArray(financialYears) ? financialYears : [];
    const cfNoCarry = Array.isArray(cfPreview?.students?.noCarry) ? cfPreview.students.noCarry : [];
    const cfCarry = Array.isArray(cfPreview?.students?.carry) ? cfPreview.students.carry : [];
    const cfGraduate = Array.isArray(cfPreview?.students?.graduate) ? cfPreview.students.graduate : [];

    const fetchAY = async () => {
        setLoadingAY(true);
        try { const r = await academicYearService.getAll(); setAcademicYears(getRows(r)); }
        catch (e) { toast.error(e.message); }
        finally { setLoadingAY(false); }
    };
    const fetchFY = async () => {
        setLoadingFY(true);
        try { const r = await financialYearService.getAll(); setFinancialYears(getRows(r)); }
        catch (e) { toast.error(e.message); }
        finally { setLoadingFY(false); }
    };

    useEffect(() => { fetchAY(); fetchFY(); }, []);

    const handleCreateAY = async (e) => {
        e.preventDefault(); setSavingAY(true);
        try {
            const r = await academicYearService.create(ayForm);
            toast.success(r.message);
            if (ayForm.setAsActive) selectAcademicYear(r.data);
            setAyForm({ label: "", startDate: "", endDate: "", notes: "", setAsActive: true });
            fetchAY();
        } catch (e) { toast.error(e.message); }
        finally { setSavingAY(false); }
    };
    const handleSetActiveAY = async (id) => {
        try { const r = await academicYearService.setActive(id); toast.success(r.message); selectAcademicYear(r.data); fetchAY(); }
        catch (e) { toast.error(e.message); }
    };
    const handleDeleteAY = async (id, label) => {
        if (!window.confirm(`Delete "${label}"?`)) return;
        try { const r = await academicYearService.remove(id); toast.success(r.message); fetchAY(); }
        catch (e) { toast.error(e.message); }
    };

    const handleCreateFY = async (e) => {
        e.preventDefault(); setSavingFY(true);
        try {
            const r = await financialYearService.create({ ...fyForm, openingBalance: fyForm.openingBalance ? Number(fyForm.openingBalance) : 0 });
            toast.success(r.message);
            if (fyForm.setAsActive) selectFinancialYear(r.data);
            setFyForm({ label: "", assessmentYearLabel: "", startDate: "", endDate: "", openingBalance: "", notes: "", setAsActive: true });
            fetchFY();
        } catch (e) { toast.error(e.message); }
        finally { setSavingFY(false); }
    };
    const handleSetActiveFY = async (id) => {
        try { const r = await financialYearService.setActive(id); toast.success(r.message); selectFinancialYear(r.data); fetchFY(); }
        catch (e) { toast.error(e.message); }
    };
    const handleDeleteFY = async (id, label) => {
        if (!window.confirm(`Delete "${label}"?`)) return;
        try { const r = await financialYearService.remove(id); toast.success(r.message); fetchFY(); }
        catch (e) { toast.error(e.message); }
    };

    const handleCFPreview = async () => {
        if (!cfSourceId) { toast.error("Select a source year."); return; }
        setLoadingCFPreview(true); setCfPreview(null); setCfResult(null);
        try { const r = await academicYearService.carryForwardPreview(cfSourceId); setCfPreview(getReportPayload(r)); }
        catch (e) { toast.error(e.message); }
        finally { setLoadingCFPreview(false); }
    };
    const handleCFExecute = async () => {
        if (!cfSourceId || !cfTargetId) { toast.error("Select both years."); return; }
        if (cfSourceId === cfTargetId) { toast.error("Source and target must differ."); return; }
        if (!window.confirm(`Carry forward students from "${academicYearRows.find(y => y._id === cfSourceId)?.label}" to "${academicYearRows.find(y => y._id === cfTargetId)?.label}"?`)) return;
        setExecutingCF(true);
        try { const r = await academicYearService.carryForward(cfSourceId, { targetYearId: cfTargetId }); toast.success(r.message); setCfResult(r.data); setCfPreview(null); }
        catch (e) { toast.error(e.message); }
        finally { setExecutingCF(false); }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImportFile(file);
        setImportResult(null);
        setImportPreviewRows(null);
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const data = new Uint8Array(evt.target.result);
                const wb = XLSX.read(data, { type: "array", cellDates: true });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const rawRows = XLSX.utils.sheet_to_json(ws, { defval: null });
                const nonEmpty = rawRows.filter((r) => Object.values(r).some((v) => v != null && v !== ""));
                const transformed = nonEmpty.map((row, i) => transformRow(row, i + 1));
                setImportPreviewRows(transformed);
                toast.success(`${transformed.length} students found in "${file.name}"`);
            } catch (err) {
                toast.error("Could not read Excel file: " + err.message);
                setImportFile(null);
            }
        };
        reader.readAsArrayBuffer(file);
        e.target.value = "";
    };

    const handleImport = async () => {
        if (!importAY) { toast.error("Please select an Academic Year for the import."); return; }
        if (!importPreviewRows || importPreviewRows.length === 0) { toast.error("No students to import. Please select an Excel file first."); return; }
        if (!window.confirm(`Import ${importPreviewRows.length} students into academic year "${academicYearRows.find(y => y._id === importAY)?.label}"?\n\nDuplicates will be skipped.`)) return;
        setImportLoading(true);
        setImportResult(null);
        try {
            const res = await studentService.importStudents(importAY, importPreviewRows);
            setImportResult(getReportPayload(res));
            toast.success(res.message);
            setImportFile(null);
            setImportPreviewRows(null);
        } catch (err) {
            toast.error("Import failed: " + err.message);
        } finally {
            setImportLoading(false);
        }
    };

    const resetImport = () => {
        setImportFile(null);
        setImportPreviewRows(null);
        setImportResult(null);
    };

    const handleEmployeeFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setEmployeeImportFile(file);
        setEmployeeImportResult(null);
        setEmployeeImportPreviewRows(null);

        const reader = new FileReader();

        reader.onload = (evt) => {
            try {
                const data = new Uint8Array(evt.target.result);
                const wb = XLSX.read(data, {
                    type: "array",
                    cellDates: true,
                });

                if (!wb.SheetNames.length) {
                    throw new Error("The Excel workbook has no worksheet.");
                }

                const ws = wb.Sheets[wb.SheetNames[0]];
                const rawRows = XLSX.utils.sheet_to_json(ws, {
                    defval: "",
                    raw: true,
                });

                const nonEmpty = rawRows.filter((row) =>
                    Object.values(row).some((v) => v !== null && v !== undefined && String(v).trim() !== "")
                );

                if (!nonEmpty.length) {
                    throw new Error("No employee records were found in the selected Excel file.");
                }

                const transformed = nonEmpty.map(transformEmployeeExcelRow);

                setEmployeeImportPreviewRows(transformed);
                toast.success(`${transformed.length} employees found in "${file.name}"`);
            } catch (err) {
                toast.error("Could not read employee Excel file: " + err.message);
                setEmployeeImportFile(null);
                setEmployeeImportPreviewRows(null);
            }
        };

        reader.readAsArrayBuffer(file);
        e.target.value = "";
    };

    const handleEmployeeImport = async () => {
        if (!employeeImportJoiningDate) {
            toast.error("Please select the joining date to use for employees where the Excel file has no Joining Date.");
            return;
        }

        if (!employeeImportPreviewRows || employeeImportPreviewRows.length === 0) {
            toast.error("No employees to import. Please select an Excel file first.");
            return;
        }

        if (
            employeeImportPreviewRows.some(
                (row) => !row.joiningDate && !employeeImportJoiningDate
            )
        ) {
            toast.error("Joining date is required.");
            return;
        }

        if (
            employeeImportPreviewRows.some(
                (row) => !row.employeeCategory
            )
        ) {
            toast.error("Could not determine employee category for one or more rows. Please add a Category column with Teaching or Non Teaching, or use supported designations such as Professor / Laboratory Technician.");
            return;
        }

        if (!window.confirm(
            `Import ${employeeImportPreviewRows.length} employees?\n\n` +
            "Existing employee codes will be skipped."
        )) return;

        setEmployeeImportLoading(true);
        setEmployeeImportResult(null);

        try {
            const res = await employeeService.importEmployees(
                employeeImportJoiningDate,
                employeeImportDefaultDepartment,
                employeeImportPreviewRows,
            );

            setEmployeeImportResult(getReportPayload(res));
            toast.success(res.message);
            setEmployeeImportFile(null);
            setEmployeeImportPreviewRows(null);
        } catch (err) {
            toast.error("Employee import failed: " + err.message);
        } finally {
            setEmployeeImportLoading(false);
        }
    };

    const resetEmployeeImport = () => {
        setEmployeeImportFile(null);
        setEmployeeImportPreviewRows(null);
        setEmployeeImportResult(null);
    };

    const ayLabel = academicYear?.label?.replace(/[/\\:*?"<>|]/g, "-") || "export";

    const handleExportStudents = async () => {
        if (!academicYear?._id) {
            toast.error("Select an academic year from the Dashboard first.");
            return;
        }

        setExportBusy("students", true);

        try {
            const res = await api.get("/students", {
                params: {
                    academicYear: academicYear._id,
                    limit: 9999,
                },
            });

            const rows = getRows(res);

            if (!rows.length) {
                toast.error("No students found for this academic year.");
                return;
            }

            const exportRows = rows.map((student, index) => ({
                sr: index + 1,
                college: student.college?._id || student.college || "",
                instituteId: student.institueId ?? "",
                academicYear:
                    student.academicYear?._id ||
                    student.academicYear ||
                    "",
                carriedForwardFrom:
                    student.carriedForwardFrom?._id ||
                    student.carriedForwardFrom ||
                    "",
                status: student.status || "",
                firstName: student.firstName || "",
                middleName: student.middleName || "",
                lastName: student.lastName || "",
                fullName: student.fullName || "",
                bloodGroup: student.bloodGroup || "",
                fatherName: student.fatherName || "",
                motherName: student.motherName || "",
                gender: student.gender || "",
                maritalStatus: student.maritalStatus || "",
                course:
                    student.course?._id ||
                    student.course ||
                    "",
                yearNumber: student.yearNumber ?? "",
                courseAdmitted:
                    student.courseAdmitted || "",
                courseYear:
                    student.courseYear || "",
                enrollmentNo:
                    student.enrollmentNo || "",
                admissionType:
                    student.admissionType || "",
                generalRegNo:
                    student.generalRegNo ?? "",
                rollNo:
                    student.rollNo ?? "",
                admissionCategory:
                    student.admissionCategory || "",
                instituteAdmissionDate:
                    student.instituteAdmissionDate || "",
                currentAdmissionDate:
                    student.currentAdmissionDate || "",
                previousPassingYear:
                    student.previousPassingYear || "",
                divisionName:
                    student.divisionName || "",
                feeStructureName:
                    student.feeStructureName || "",
                studentType:
                    student.studentType || "",
                firstYearStream:
                    student.firstYearStream || "",
                feesDecided:
                    student.feesDecided ?? "",
                scholarshipAmountBalance:
                    student.scholarshipAmountBalance ?? "",
                aadharNo:
                    student.aadharNo || "",
                aaparId:
                    student.aaparId || "",
                studentPhotoUrl:
                    student.studentPhoto?.url || "",
                studentPhotoPublicId:
                    student.studentPhoto?.publicId || "",
                dob:
                    student.dob || "",
                dobInWords:
                    student.dobInWords || "",
                nationality:
                    student.nationality || "",
                motherTongue:
                    student.motherTongue || "",
                religion:
                    student.religion || "",
                caste:
                    student.caste || "",
                subCaste:
                    student.subCaste || "",
                isBplCardHolder:
                    student.isBplCardHolder ?? "",
                bplCardNo:
                    student.bplCardNo || "",
                rationCardNo:
                    student.rationCardNo || "",
                previousSchoolName:
                    student.previousSchoolName || "",
                placeOfBirth:
                    student.placeOfBirth || "",
                birthDistrict:
                    student.birthDistrict || "",
                birthTaluka:
                    student.birthTaluka || "",
                birthState:
                    student.birthState || "",
                birthCountry:
                    student.birthCountry || "",
                birthUrbanRural:
                    student.birthUrbanRural || "",
                mobile:
                    student.mobile || "",
                fatherMobile:
                    student.fatherMobile || "",
                motherMobile:
                    student.motherMobile || "",
                telephoneNo:
                    student.telephoneNo || "",
                parentAddress:
                    student.parentAddress || "",
                parentIncome:
                    student.parentIncome ?? "",
                motherIncome:
                    student.motherIncome ?? "",
                parentEmailAddress:
                    student.parentEmailAddress || "",
                parentOccupation:
                    student.parentOccupation || "",
                motherOccupation:
                    student.motherOccupation || "",
                fatherDob:
                    student.fatherDob || "",
                motherDob:
                    student.motherDob || "",
                fatherEduction:
                    student.fatherEduction || "",
                motherEducation:
                    student.motherEducation || "",
                email:
                    student.email || "",
                address:
                    student.address || "",
                city:
                    student.city || "",
                taluka:
                    student.taluka || "",
                addressUrbanRural:
                    student.addressUrbanRural || "",
                district:
                    student.district || "",
                state:
                    student.state || "",
                pincode:
                    student.pincode || "",
                country:
                    student.country || "",
                bankAccountNo:
                    student.bankAccountNo || "",
                bankName:
                    student.bankName || "",
                branchName:
                    student.branchName || "",
                ifscCode:
                    student.ifscCode || "",
                createdAt:
                    student.createdAt || "",
                updatedAt:
                    student.updatedAt || "",
            }));

            exportToExcel(
                exportRows,
                [
                    { header: "Sr.", key: "sr" },
                    { header: "college", key: "college" },
                    { header: "instituteId", key: "instituteId" },
                    { header: "academicYear", key: "academicYear" },
                    { header: "carriedForwardFrom", key: "carriedForwardFrom" },
                    { header: "Status", key: "status" },
                    { header: "firstName", key: "firstName" },
                    { header: "middleName", key: "middleName" },
                    { header: "lastName", key: "lastName" },
                    { header: "fullName", key: "fullName" },
                    { header: "bloodGroup", key: "bloodGroup" },
                    { header: "fatherName", key: "fatherName" },
                    { header: "motherName", key: "motherName" },
                    { header: "gender", key: "gender" },
                    { header: "maritalStatus", key: "maritalStatus" },
                    { header: "course", key: "course" },
                    { header: "yearNumber", key: "yearNumber" },
                    { header: "courseAdmitted", key: "courseAdmitted" },
                    { header: "courseYear", key: "courseYear" },
                    { header: "enrollmentNo", key: "enrollmentNo" },
                    { header: "admissionType", key: "admissionType" },
                    { header: "generalRegNo", key: "generalRegNo" },
                    { header: "rollNo", key: "rollNo" },
                    { header: "admissionCategory", key: "admissionCategory" },
                    { header: "instituteAdmissionDate", key: "instituteAdmissionDate" },
                    { header: "currentAdmissionDate", key: "currentAdmissionDate" },
                    { header: "previousPassingYear", key: "previousPassingYear" },
                    { header: "divisionName", key: "divisionName" },
                    { header: "feeStructureName", key: "feeStructureName" },
                    { header: "studentType", key: "studentType" },
                    { header: "firstYearStream", key: "firstYearStream" },
                    { header: "feesDecided", key: "feesDecided" },
                    {
                        header: "scholarshipAmountBalance",
                        key: "scholarshipAmountBalance",
                    },
                    { header: "aadharNo", key: "aadharNo" },
                    { header: "aaparId", key: "aaparId" },
                    { header: "studentPhotoUrl", key: "studentPhotoUrl" },
                    { header: "studentPhotoPublicId", key: "studentPhotoPublicId" },
                    { header: "dob", key: "dob" },
                    { header: "dobInWords", key: "dobInWords" },
                    { header: "nationality", key: "nationality" },
                    { header: "motherTongue", key: "motherTongue" },
                    { header: "religion", key: "religion" },
                    { header: "caste", key: "caste" },
                    { header: "subCaste", key: "subCaste" },
                    { header: "isBplCardHolder", key: "isBplCardHolder" },
                    { header: "bplCardNo", key: "bplCardNo" },
                    { header: "rationCardNo", key: "rationCardNo" },
                    { header: "previousSchoolName", key: "previousSchoolName" },
                    { header: "placeOfBirth", key: "placeOfBirth" },
                    { header: "birthDistrict", key: "birthDistrict" },
                    { header: "birthTaluka", key: "birthTaluka" },
                    { header: "birthState", key: "birthState" },
                    { header: "birthCountry", key: "birthCountry" },
                    { header: "birthUrbanRural", key: "birthUrbanRural" },
                    { header: "mobile", key: "mobile" },
                    { header: "fatherMobile", key: "fatherMobile" },
                    { header: "motherMobile", key: "motherMobile" },
                    { header: "telephoneNo", key: "telephoneNo" },
                    { header: "parentAddress", key: "parentAddress" },
                    { header: "parentIncome", key: "parentIncome" },
                    { header: "motherIncome", key: "motherIncome" },
                    { header: "parentEmailAddress", key: "parentEmailAddress" },
                    { header: "parentOccupation", key: "parentOccupation" },
                    { header: "motherOccupation", key: "motherOccupation" },
                    { header: "fatherDob", key: "fatherDob" },
                    { header: "motherDob", key: "motherDob" },
                    { header: "fatherEduction", key: "fatherEduction" },
                    { header: "motherEducation", key: "motherEducation" },
                    { header: "email", key: "email" },
                    { header: "address", key: "address" },
                    { header: "city", key: "city" },
                    { header: "taluka", key: "taluka" },
                    { header: "addressUrbanRural", key: "addressUrbanRural" },
                    { header: "district", key: "district" },
                    { header: "state", key: "state" },
                    { header: "pincode", key: "pincode" },
                    { header: "country", key: "country" },
                    { header: "bankAccountNo", key: "bankAccountNo" },
                    { header: "bankName", key: "bankName" },
                    { header: "branchName", key: "branchName" },
                    { header: "IFSC Code", key: "ifscCode" },
                    { header: "createdAt", key: "createdAt" },
                    { header: "updatedAt", key: "updatedAt" },
                ],
                `Students_${ayLabel}`
            );

            toast.success(`${rows.length} students exported.`);
        } catch (e) {
            toast.error("Export failed: " + e.message);
        } finally {
            setExportBusy("students", false);
        }
    };

    const handleExportEmployees = async () => {
        setExportBusy("employees", true);
        try {
            const res = await api.get("/employees", { params: { limit: 9999 } });
            const rows = getRows(res);
            if (!rows.length) { toast.error("No employees found."); return; }

            exportToExcel(rows, [
                { header: "Sr.", key: "sr" },
                { header: "Full Name", key: "fullName" },
                { header: "Employee Code", key: "employeeCode" },
                { header: "Category", key: "employeeCategory" },
                { header: "Department", key: "department" },
                { header: "Designation", key: "designation" },
                { header: "Qualification", key: "qualification" },
                { header: "Experience", key: "experience" },
                { header: "Gender", key: "gender" },
                { header: "Status", key: "status" },
                { header: "Mobile", key: "mobile" },
                { header: "Email", key: "email" },
                { header: "Joining Date", key: "joiningDate" },
                { header: "Date of Birth", key: "dateOfBirth" },
                { header: "Address", key: "address" },
            ], "Employees_Register");

            toast.success(`${rows.length} employees exported.`);
        } catch (e) {
            toast.error("Export failed: " + e.message);
        } finally {
            setExportBusy("employees", false);
        }
    };

    const handleExportFees = async () => {
        if (!academicYear?._id) { toast.error("Select an academic year from the Dashboard first."); return; }
        setExportBusy("fees", true);
        try {
            const res = await api.get("/fees/receipts", { params: { academicYear: academicYear._id, limit: 9999 } });
            const rows = getRows(res);
            if (!rows.length) { toast.error("No fee receipts found for this academic year."); return; }

            const flat = rows.map((r) => ({
                receiptNo: r.receiptNo,
                studentName: r.student?.fullName || "",
                enrollmentNo: r.student?.enrollmentNo || "",
                course: r.student?.course?.shortName || r.student?.course?.courseName || "Course",
                courseYear: r.student?.yearNumber ? `Year ${r.student.yearNumber}` : "",
                installment: r.installmentLabel || r.installmentNo,
                amountPaid: r.amountPaid,
                paymentMode: r.paymentMode,
                paymentDate: r.paymentDate,
                chequeNo: r.chequeNo || "",
                bankName: r.bankName || "",
                transactionRef: r.transactionRef || "",
                remarks: r.remarks || "",
            }));

            exportToExcel(flat, [
                { header: "Receipt No.", key: "receiptNo" },
                { header: "Student Name", key: "studentName" },
                { header: "Enrollment No.", key: "enrollmentNo" },
                { header: "Course", key: "course" },
                { header: "Year", key: "courseYear" },
                { header: "Installment", key: "installment" },
                { header: "Amount Paid (₹)", key: "amountPaid" },
                { header: "Payment Mode", key: "paymentMode" },
                { header: "Payment Date", key: "paymentDate" },
                { header: "Cheque No.", key: "chequeNo" },
                { header: "Bank Name", key: "bankName" },
                { header: "Transaction Ref.", key: "transactionRef" },
                { header: "Remarks", key: "remarks" },
            ], `Fee_Receipts_${ayLabel}`);

            toast.success(`${rows.length} receipts exported.`);
        } catch (e) {
            toast.error("Export failed: " + e.message);
        } finally {
            setExportBusy("fees", false);
        }
    };

    const handleExportBonafide = async () => {
        if (!academicYear?._id) { toast.error("Select an academic year from the Dashboard first."); return; }
        setExportBusy("bonafide", true);
        try {
            const res = await api.get("/bonafide", { params: { academicYear: academicYear._id, limit: 9999 } });
            const rows = getRows(res);
            if (!rows.length) { toast.error("No bonafide certificates found."); return; }

            const flat = rows.map((r) => ({
                srNo: r.srNo,
                studentName: r.student?.fullName || "",
                enrollmentNo: r.student?.enrollmentNo || "",
                className: r.className || "",
                sessionStart: r.sessionStart || "",
                sessionEnd: r.sessionEnd || "",
                certificateDate: r.certificateDate || "",
            }));

            exportToExcel(flat, [
                { header: "Sr. No.", key: "srNo" },
                { header: "Student Name", key: "studentName" },
                { header: "Enrollment No.", key: "enrollmentNo" },
                { header: "Class", key: "className" },
                { header: "Session Start", key: "sessionStart" },
                { header: "Session End", key: "sessionEnd" },
                { header: "Certificate Date", key: "certificateDate" },
            ], `Bonafide_Certificates_${ayLabel}`);

            toast.success(`${rows.length} certificates exported.`);
        } catch (e) {
            toast.error("Export failed: " + e.message);
        } finally {
            setExportBusy("bonafide", false);
        }
    };

    const handleExportLeaving = async () => {
        setExportBusy("leaving", true);
        try {
            const res = await api.get("/leaving", { params: { limit: 9999 } });
            const rows = getRows(res);
            if (!rows.length) { toast.error("No leaving certificates found."); return; }

            const flat = rows.map((r) => ({
                serialNo: r.serialNo,
                studentName: r.student?.fullName || "",
                enrollmentNo: r.student?.enrollmentNo || "",
                dateOfLeaving: r.dateOfLeaving || "",
                reasonOfLeaving: r.reasonOfLeaving || "",
                dateOfIssue: r.dateOfIssue || "",
                standard: r.standard || "",
                progress: r.progress || "",
                conduct: r.conduct || "",
            }));

            exportToExcel(flat, [
                { header: "Serial No.", key: "serialNo" },
                { header: "Student Name", key: "studentName" },
                { header: "Enrollment No.", key: "enrollmentNo" },
                { header: "Standard", key: "standard" },
                { header: "Date of Leaving", key: "dateOfLeaving" },
                { header: "Reason of Leaving", key: "reasonOfLeaving" },
                { header: "Progress", key: "progress" },
                { header: "Conduct", key: "conduct" },
                { header: "Date of Issue", key: "dateOfIssue" },
            ], "Leaving_Certificates");

            toast.success(`${rows.length} certificates exported.`);
        } catch (e) {
            toast.error("Export failed: " + e.message);
        } finally {
            setExportBusy("leaving", false);
        }
    };

    const handleExportSalary = async () => {
        setExportBusy("salary", true);
        try {
            const res = await api.get("/salary", { params: { month: salaryExportMonth, year: salaryExportYear } });
            const rows = getRows(res);
            if (!rows.length) {
                toast.error(`No salary records for ${MONTHS[salaryExportMonth]} ${salaryExportYear}.`);
                return;
            }

            const flat = rows.map((r) => ({
                employeeName: r.employee?.fullName || "",
                employeeCode: r.employee?.employeeCode || "",
                department: r.employee?.department || "",
                designation: r.employee?.designation || "",
                workingDays: r.workingDays,
                presentDays: r.presentDays,
                lopDays: r.lopDays,
                paidLeaveDays: r.paidLeaveDays,
                basicPay: r.basicPay,
                da: r.da,
                hra: r.hra,
                otherAllowances: r.otherAllowances,
                grossPay: r.grossPay,
                lopDeduction: r.lopDeduction,
                pf: r.pf,
                pt: r.pt,
                tds: r.tds,
                otherDeductions: r.otherDeductions,
                totalDeductions: r.totalDeductions,
                netPay: r.netPay,
                status: r.status,
            }));

            exportToExcel(flat, [
                { header: "Employee Name", key: "employeeName" },
                { header: "Employee Code", key: "employeeCode" },
                { header: "Department", key: "department" },
                { header: "Designation", key: "designation" },
                { header: "Working Days", key: "workingDays" },
                { header: "Present Days", key: "presentDays" },
                { header: "LOP Days", key: "lopDays" },
                { header: "Paid Leave", key: "paidLeaveDays" },
                { header: "Basic Pay (₹)", key: "basicPay" },
                { header: "DA (₹)", key: "da" },
                { header: "HRA (₹)", key: "hra" },
                { header: "Other Allow. (₹)", key: "otherAllowances" },
                { header: "Gross Pay (₹)", key: "grossPay" },
                { header: "LOP Deduction (₹)", key: "lopDeduction" },
                { header: "PF (₹)", key: "pf" },
                { header: "PT (₹)", key: "pt" },
                { header: "TDS (₹)", key: "tds" },
                { header: "Other Ded. (₹)", key: "otherDeductions" },
                { header: "Total Ded. (₹)", key: "totalDeductions" },
                { header: "Net Pay (₹)", key: "netPay" },
                { header: "Status", key: "status" },
            ], `Salary_${MONTHS[salaryExportMonth]}_${salaryExportYear}`);

            toast.success(`${rows.length} salary records exported.`);
        } catch (e) {
            toast.error("Export failed: " + e.message);
        } finally {
            setExportBusy("salary", false);
        }
    };

    const handleExportAttendance = async () => {
        if (!academicYear?._id) { toast.error("Select an academic year from the Dashboard first."); return; }
        setExportBusy("attendance", true);
        try {
            const res = await api.get("/attendance/report", {
                params: { academicYear: academicYear._id, reportType: "yearly" },
            });
            const report = getReportPayload(res);
            const summaries = Array.isArray(report.studentSummaries) ? report.studentSummaries : [];
            if (!summaries.length) { toast.error("No attendance data found."); return; }

            const flat = summaries.map((s) => ({
                fullName: s.student?.fullName || "",
                enrollmentNo: s.student?.enrollmentNo || "",
                course: s.student?.course?.shortName || s.student?.course?.courseName || "Course",
                courseYear: s.student?.yearNumber ? `Year ${s.student.yearNumber}` : "",
                totalDays: s.totalDays,
                presentDays: s.presentDays,
                absentDays: s.absentDays,
                leaveDays: s.leaveDays,
                lateDays: s.lateDays,
                percentage: s.attendancePercentage,
            }));

            exportToExcel(flat, [
                { header: "Student Name", key: "fullName" },
                { header: "Enrollment No.", key: "enrollmentNo" },
                { header: "Course", key: "course" },
                { header: "Year", key: "courseYear" },
                { header: "Total Days", key: "totalDays" },
                { header: "Present Days", key: "presentDays" },
                { header: "Absent Days", key: "absentDays" },
                { header: "Leave Days", key: "leaveDays" },
                { header: "Late Days", key: "lateDays" },
                { header: "Attendance %", key: "percentage" },
            ], `Student_Attendance_${ayLabel}`);

            toast.success(`${flat.length} student attendance records exported.`);
        } catch (e) {
            toast.error("Export failed: " + e.message);
        } finally {
            setExportBusy("attendance", false);
        }
    };

    const handleExportExams = async () => {
        if (!academicYear?._id) { toast.error("Select an academic year from the Dashboard first."); return; }
        setExportBusy("exams", true);
        try {
            const examsRes = await api.get("/exams", { params: { academicYear: academicYear._id } });
            const exams = getRows(examsRes);
            if (!exams.length) { toast.error("No exams found for this academic year."); return; }

            const flat = exams.map((ex) => ({
                title: ex.title,
                examType: ex.examType,
                course: ex.course?.shortName || ex.course?.courseName || ex.courseAdmitted || "Course",
                courseYear: ex.yearNumber ? `Year ${ex.yearNumber}` : ex.courseYear || "",
                examDate: ex.examDate || "",
                subjects: ex.subjects?.map((s) => s.name).join(", ") || "",
                totalMax: ex.subjects?.reduce((s, sub) => s + sub.maxMarks, 0) || 0,
                status: ex.status,
                appeared: ex.resultCount || 0,
                passed: ex.passCount || 0,
                passRate: ex.resultCount
                    ? `${Math.round((ex.passCount / ex.resultCount) * 100)}%`
                    : "—",
            }));

            exportToExcel(flat, [
                { header: "Exam Title", key: "title" },
                { header: "Type", key: "examType" },
                { header: "Course", key: "course" },
                { header: "Year", key: "courseYear" },
                { header: "Exam Date", key: "examDate" },
                { header: "Subjects", key: "subjects" },
                { header: "Total Marks", key: "totalMax" },
                { header: "Status", key: "status" },
                { header: "Appeared", key: "appeared" },
                { header: "Passed", key: "passed" },
                { header: "Pass Rate", key: "passRate" },
            ], `Exams_Summary_${ayLabel}`);

            toast.success(`${flat.length} exam records exported.`);
        } catch (e) {
            toast.error("Export failed: " + e.message);
        } finally {
            setExportBusy("exams", false);
        }
    };

    const handleExportAll = async () => {
        if (!window.confirm("Export all available data to separate Excel files? This may take a moment.")) return;
        await handleExportStudents();
        await handleExportEmployees();
        await handleExportFees();
        await handleExportBonafide();
        await handleExportLeaving();
        await handleExportAttendance();
        await handleExportExams();
        toast.success("All exports complete.");
    };

    const nowYear = new Date().getFullYear();
    const yearOpts = Array.from({ length: 5 }, (_, i) => nowYear - i);
    const noAY = !academicYear?._id;

    return (
        <div style={{ maxWidth: 1180 }}>
            <div className="page-header">
                <div>
                    <h1 className="page-title">Settings</h1>
                    <p className="page-subtitle">Manage academic years, financial years, carry forward, student/employee import, certificate templates and data export</p>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════════
          CERTIFICATE TEMPLATES & CALIBRATION
      ══════════════════════════════════════════════════════════════════════ */}
            <div className="card" style={{ marginBottom: "1.25rem" }}>
                <div className="card-header" style={{ borderLeft: "4px solid #7c3aed", paddingLeft: "0.9rem", fontWeight: 700 }}>
                    📜 Certificate Print Templates & Calibration
                </div>
                <div className="card-body">
                    <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: "1rem", lineHeight: 1.6 }}>
                        Configure the pre-printed template background images, paper dimensions, and text coordinate positions for each certificate type in your college.
                    </p>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                            gap: "1rem",
                        }}
                    >
                        {/* Bonafide Template */}
                        <div
                            className="card"
                            onClick={() => navigate("/settings/bonafide-template")}
                            style={{
                                padding: "1rem 1.25rem",
                                border: "1px solid var(--border)",
                                borderRadius: "var(--radius)",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                transition: "all 0.15s",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#7c3aed")}
                            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--border)")}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
                                <div
                                    style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 10,
                                        background: "#ede9fe",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#7c3aed",
                                        flexShrink: 0,
                                    }}
                                >
                                    <FiAward size={20} />
                                </div>
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 14 }}>Bonafide Template</div>
                                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                                        Upload template image & calibrate coordinates
                                    </div>
                                </div>
                            </div>
                            <FiChevronRight size={18} color="var(--text-muted)" />
                        </div>

                        {/* Leaving Certificate Template */}
                        <div
                            className="card"
                            onClick={() => navigate("/settings/leaving-template")}
                            style={{
                                padding: "1rem 1.25rem",
                                border: "1px solid var(--border)",
                                borderRadius: "var(--radius)",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                transition: "all 0.15s",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#0284c7")}
                            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--border)")}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
                                <div
                                    style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 10,
                                        background: "#e0f2fe",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#0284c7",
                                        flexShrink: 0,
                                    }}
                                >
                                    <FiFileText size={20} />
                                </div>
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 14 }}>Leaving Certificate Template</div>
                                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                                        Upload physical TC form & configure positions
                                    </div>
                                </div>
                            </div>
                            <FiChevronRight size={18} color="var(--text-muted)" />
                        </div>
                    </div>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════════
          IMPORT STUDENTS
      ══════════════════════════════════════════════════════════════════════ */}
            <div className="card" style={{ marginBottom: "1.25rem" }}>
                <div className="card-header" style={{ borderLeft: "4px solid #7c3aed", paddingLeft: "0.9rem" }}>
                    📥 Import Students from Excel
                </div>
                <div className="card-body">
                    <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: "1rem", lineHeight: 1.6 }}>
                        Select an Excel file (.xlsx) with the student migration format. The file is read in your
                        browser, transformed, and sent directly to the database. Existing students
                        (matched by Enrollment No. + Academic Year) are automatically skipped.
                    </p>

                    <div className="form-group" style={{ marginBottom: "1rem", maxWidth: 320 }}>
                        <label className="form-label">Target Academic Year <span style={{ color: "var(--danger)" }}>*</span></label>
                        <select className="form-control" value={importAY} onChange={(e) => setImportAY(e.target.value)}>
                            <option value="">— Select academic year —</option>
                            {academicYearRows.map((y) => (
                                <option key={y._id} value={y._id}>{y.label}{y.isActive ? " ✓" : ""}</option>
                            ))}
                        </select>
                        <span className="form-hint">Students will be imported into this year</span>
                    </div>

                    <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={handleFileSelect} />

                    {!importPreviewRows && !importResult && (
                        <div
                            onClick={() => fileInputRef.current?.click()}
                            style={{ border: "2px dashed #c4b5fd", borderRadius: "10px", padding: "2rem", textAlign: "center", cursor: "pointer", background: "#faf5ff", transition: "background 0.15s", marginBottom: "1rem" }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "#f3e8ff")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "#faf5ff")}
                        >
                            <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>📂</div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: "#6d28d9" }}>Click to select Excel file</div>
                            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: "4px" }}>Supported: .xlsx, .xls — StudentMigrationSheetSampleFormat format</div>
                        </div>
                    )}

                    {importPreviewRows && !importResult && (
                        <div style={{ background: "#f5f3ff", border: "1px solid #c4b5fd", borderRadius: 8, padding: "1rem", marginBottom: "1rem" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 14, color: "#6d28d9" }}>✅ {importPreviewRows.length} students ready to import</div>
                                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>File: {importFile?.name}</div>
                                </div>
                                <button className="btn btn-outline btn-sm" onClick={resetImport}>✕ Clear</button>
                            </div>
                            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: "0.5rem" }}>Preview (first 5 students):</div>
                            <div className="table-wrapper" style={{ maxHeight: 200, overflowY: "auto" }}>
                                <table style={{ fontSize: 12 }}>
                                    <thead><tr><th>#</th><th>Full Name</th><th>Enrollment No.</th><th>Course</th><th>Category</th><th>DOB</th></tr></thead>
                                    <tbody>
                                        {importPreviewRows.slice(0, 5).map((r, i) => (
                                            <tr key={i}>
                                                <td>{i + 1}</td><td>{r.fullName}</td>
                                                <td style={{ fontFamily: "monospace" }}>{r.enrollmentNo}</td>
                                                <td>{r.course?.shortName || r.course || r.courseAdmitted} {r.yearNumber ? `Year ${r.yearNumber}` : r.courseYear}</td>
                                                <td>{r.admissionCategory}</td><td>{r.dob}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {importPreviewRows.length > 5 && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>+ {importPreviewRows.length - 5} more…</div>}
                            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
                                <button className="btn btn-primary" onClick={handleImport} disabled={importLoading || !importAY}>
                                    {importLoading ? `Importing ${importPreviewRows.length} students…` : `▶ Import ${importPreviewRows.length} Students`}
                                </button>
                                <button className="btn btn-outline" onClick={() => fileInputRef.current?.click()}>Change File</button>
                            </div>
                            {!importAY && <div style={{ fontSize: 12, color: "var(--danger)", marginTop: "0.5rem" }}>⚠️ Select an academic year above before importing.</div>}
                        </div>
                    )}

                    {importResult && (
                        <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "1rem", marginBottom: "1rem" }}>
                            <div style={{ fontWeight: 600, fontSize: 14, color: "#15803d", marginBottom: "0.75rem" }}>✅ Import Complete</div>
                            <div style={{ display: "flex", gap: "1.5rem", fontSize: 13, marginBottom: "0.75rem" }}>
                                <span>Total rows: <strong>{importResult.totalRows}</strong></span>
                                <span style={{ color: "#15803d" }}>Imported: <strong>{importResult.imported}</strong></span>
                                <span style={{ color: "#d97706" }}>Skipped: <strong>{importResult.skipped}</strong></span>
                                {importResult.errors > 0 && <span style={{ color: "#dc2626" }}>Errors: <strong>{importResult.errors}</strong></span>}
                            </div>
                            {importResult.errorList?.length > 0 && (
                                <div style={{ marginBottom: "0.75rem" }}>
                                    <div style={{ fontSize: 12, color: "#dc2626", fontWeight: 600, marginBottom: "0.3rem" }}>Errors (not imported):</div>
                                    <div style={{ maxHeight: 150, overflowY: "auto", fontSize: 12 }}>
                                        {importResult.errorList.map((e, i) => (
                                            <div key={i} style={{ color: "#dc2626", padding: "1px 0" }}>❌ Row {e.index} — {e.fullName} ({e.enrollmentNo}): {e.reason}</div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {importResult.skipList?.length > 0 && (
                                <details style={{ fontSize: 12 }}>
                                    <summary style={{ cursor: "pointer", color: "#d97706", fontWeight: 600, marginBottom: "0.25rem" }}>{importResult.skipped} skipped (already exist)</summary>
                                    <div style={{ maxHeight: 120, overflowY: "auto", marginTop: "0.25rem" }}>
                                        {importResult.skipList.map((s, i) => (
                                            <div key={i} style={{ color: "var(--text-muted)", padding: "1px 0" }}>⏭ {s.fullName} ({s.enrollmentNo})</div>
                                        ))}
                                    </div>
                                </details>
                            )}
                            <button className="btn btn-outline btn-sm" style={{ marginTop: "0.75rem" }} onClick={resetImport}>Import Another File</button>
                        </div>
                    )}
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════════
          IMPORT EMPLOYEES
      ══════════════════════════════════════════════════════════════════════ */}
            <div className="card" style={{ marginBottom: "1.25rem" }}>
                <div className="card-header" style={{ borderLeft: "4px solid #0891b2", paddingLeft: "0.9rem" }}>
                    📥 Import Employees from Excel
                </div>
                <div className="card-body">
                    <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: "1rem", lineHeight: 1.6 }}>
                        Select an Excel file containing employee records. The file is read in your browser,
                        converted to the Employee format, and sent to the database. Existing employee codes
                        are automatically skipped. CICOP sheets are supported, including columns such as
                        <strong> Faculty, Pharmacy Council No., Qualification, Designation, Department,
                            Overall Experience, Email and Mobile No.</strong>
                    </p>

                    <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 320px) minmax(220px, 320px)", gap: "1rem", marginBottom: "1rem" }}>
                        <div className="form-group">
                            <label className="form-label">
                                Default Joining Date <span style={{ color: "var(--danger)" }}>*</span>
                            </label>
                            <input
                                type="date"
                                className="form-control"
                                value={employeeImportJoiningDate}
                                onChange={(e) => setEmployeeImportJoiningDate(e.target.value)}
                            />
                            <span className="form-hint">
                                Used when the Excel row does not contain a Joining Date.
                            </span>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Default Department</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Optional — e.g. M.Pharm Pharmacy"
                                value={employeeImportDefaultDepartment}
                                onChange={(e) => setEmployeeImportDefaultDepartment(e.target.value)}
                            />
                            <span className="form-hint">
                                Used only for rows where Department is blank.
                            </span>
                        </div>
                    </div>

                    <input
                        ref={employeeFileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        style={{ display: "none" }}
                        onChange={handleEmployeeFileSelect}
                    />

                    {!employeeImportPreviewRows && !employeeImportResult && (
                        <div
                            onClick={() => employeeFileInputRef.current?.click()}
                            style={{ border: "2px dashed #67e8f9", borderRadius: "10px", padding: "2rem", textAlign: "center", cursor: "pointer", background: "#ecfeff", transition: "background 0.15s", marginBottom: "1rem" }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "#cffafe")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "#ecfeff")}
                        >
                            <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>📂</div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: "#0e7490" }}>Click to select Excel file</div>
                            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: "4px" }}>
                                Supported: .xlsx, .xls — CICOP or Employee Register format
                            </div>
                        </div>
                    )}

                    {employeeImportPreviewRows && !employeeImportResult && (
                        <div style={{ background: "#ecfeff", border: "1px solid #67e8f9", borderRadius: 8, padding: "1rem", marginBottom: "1rem" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 14, color: "#0e7490" }}>
                                        ✅ {employeeImportPreviewRows.length} employees ready to import
                                    </div>
                                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                                        File: {employeeImportFile?.name}
                                    </div>
                                </div>
                                <button className="btn btn-outline btn-sm" onClick={resetEmployeeImport}>✕ Clear</button>
                            </div>

                            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: "0.5rem" }}>
                                Preview (first 5 employees):
                            </div>

                            <div className="table-wrapper" style={{ maxHeight: 220, overflowY: "auto" }}>
                                <table style={{ fontSize: 12 }}>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Faculty</th>
                                            <th>Employee Code</th>
                                            <th>Category</th>
                                            <th>Designation</th>
                                            <th>Department</th>
                                            <th>Mobile</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {employeeImportPreviewRows.slice(0, 5).map((r, i) => (
                                            <tr key={i}>
                                                <td>{i + 1}</td>
                                                <td>{r.fullName || "—"}</td>
                                                <td style={{ fontFamily: "monospace" }}>{r.employeeCode || "—"}</td>
                                                <td>{r.employeeCategory || "—"}</td>
                                                <td>{r.designation || "—"}</td>
                                                <td>{r.department || employeeImportDefaultDepartment || "—"}</td>
                                                <td>{r.mobile || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {employeeImportPreviewRows.length > 5 && (
                                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
                                    + {employeeImportPreviewRows.length - 5} more…
                                </div>
                            )}

                            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleEmployeeImport}
                                    disabled={employeeImportLoading || !employeeImportJoiningDate}
                                >
                                    {employeeImportLoading
                                        ? `Importing ${employeeImportPreviewRows.length} employees…`
                                        : `▶ Import ${employeeImportPreviewRows.length} Employees`}
                                </button>
                                <button
                                    className="btn btn-outline"
                                    onClick={() => employeeFileInputRef.current?.click()}
                                >
                                    Change File
                                </button>
                            </div>

                            {!employeeImportJoiningDate && (
                                <div style={{ fontSize: 12, color: "var(--danger)", marginTop: "0.5rem" }}>
                                    ⚠️ Select the default joining date before importing.
                                </div>
                            )}
                        </div>
                    )}

                    {employeeImportResult && (
                        <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "1rem", marginBottom: "1rem" }}>
                            <div style={{ fontWeight: 600, fontSize: 14, color: "#15803d", marginBottom: "0.75rem" }}>
                                ✅ Employee Import Complete
                            </div>

                            <div style={{ display: "flex", gap: "1.5rem", fontSize: 13, marginBottom: "0.75rem", flexWrap: "wrap" }}>
                                <span>Total rows: <strong>{employeeImportResult.totalRows}</strong></span>
                                <span style={{ color: "#15803d" }}>Imported: <strong>{employeeImportResult.imported}</strong></span>
                                <span style={{ color: "#d97706" }}>Skipped: <strong>{employeeImportResult.skipped}</strong></span>
                                {employeeImportResult.errors > 0 && (
                                    <span style={{ color: "#dc2626" }}>Errors: <strong>{employeeImportResult.errors}</strong></span>
                                )}
                            </div>

                            {employeeImportResult.errorList?.length > 0 && (
                                <div style={{ marginBottom: "0.75rem" }}>
                                    <div style={{ fontSize: 12, color: "#dc2626", fontWeight: 600, marginBottom: "0.3rem" }}>
                                        Errors (not imported):
                                    </div>
                                    <div style={{ maxHeight: 180, overflowY: "auto", fontSize: 12 }}>
                                        {employeeImportResult.errorList.map((e, i) => (
                                            <div key={i} style={{ color: "#dc2626", padding: "1px 0" }}>
                                                ❌ Row {e.index} — {e.fullName} ({e.employeeCode}): {e.reason}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {employeeImportResult.skipList?.length > 0 && (
                                <details style={{ fontSize: 12 }}>
                                    <summary style={{ cursor: "pointer", color: "#d97706", fontWeight: 600, marginBottom: "0.25rem" }}>
                                        {employeeImportResult.skipped} skipped
                                    </summary>
                                    <div style={{ maxHeight: 150, overflowY: "auto", marginTop: "0.25rem" }}>
                                        {employeeImportResult.skipList.map((s, i) => (
                                            <div key={i} style={{ color: "var(--text-muted)", padding: "1px 0" }}>
                                                ⏭ {s.fullName} ({s.employeeCode}) — {s.reason}
                                            </div>
                                        ))}
                                    </div>
                                </details>
                            )}

                            <button
                                className="btn btn-outline btn-sm"
                                style={{ marginTop: "0.75rem" }}
                                onClick={resetEmployeeImport}
                            >
                                Import Another File
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════════
          ACADEMIC YEARS
      ══════════════════════════════════════════════════════════════════════ */}
            <div className="card" style={{ marginBottom: "1.25rem" }}>
                <div className="card-header" style={{ borderLeft: "4px solid #003b67", paddingLeft: "0.9rem" }}>
                    📅 Academic Years
                </div>
                <div className="card-body">
                    {loadingAY ? (
                        <div className="loading-center" style={{ padding: "1rem" }}><div className="spinner" /></div>
                    ) : academicYearRows.length === 0 ? (
                        <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: "1rem" }}>No academic years yet.</p>
                    ) : (
                        <div style={{ marginBottom: "1.25rem" }}>
                            {academicYearRows.map((y) => (
                                <YearCard key={y._id} year={y} type="academic" isActive={y.isActive} onSetActive={handleSetActiveAY} onDelete={handleDeleteAY} />
                            ))}
                        </div>
                    )}
                    <div style={{ borderTop: "1px solid var(--border)", paddingTop: "1rem" }}>
                        <p style={{ fontWeight: 600, fontSize: 13, marginBottom: "0.75rem" }}>Create New Academic Year</p>
                        <form onSubmit={handleCreateAY}>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label className="form-label">Label *</label>
                                    <input className="form-control" placeholder="e.g. 2026-2027" value={ayForm.label} onChange={(e) => setAyForm((p) => ({ ...p, label: e.target.value }))} required />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Start Date *</label>
                                    <input type="date" className="form-control" value={ayForm.startDate} onChange={(e) => setAyForm((p) => ({ ...p, startDate: e.target.value }))} required />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">End Date *</label>
                                    <input type="date" className="form-control" value={ayForm.endDate} onChange={(e) => setAyForm((p) => ({ ...p, endDate: e.target.value }))} required />
                                </div>
                            </div>
                            <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: 13, marginTop: "0.5rem", cursor: "pointer" }}>
                                <input type="checkbox" checked={ayForm.setAsActive} onChange={(e) => setAyForm((p) => ({ ...p, setAsActive: e.target.checked }))} />
                                Set as active year immediately
                            </label>
                            <div style={{ marginTop: "0.75rem" }}>
                                <button type="submit" className="btn btn-primary" disabled={savingAY}>{savingAY ? "Creating…" : "Create Academic Year"}</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════════
          FINANCIAL YEARS
      ══════════════════════════════════════════════════════════════════════ */}
            <div className="card" style={{ marginBottom: "1.25rem" }}>
                <div className="card-header" style={{ borderLeft: "4px solid #d97706", paddingLeft: "0.9rem" }}>
                    💰 Financial Years
                </div>
                <div className="card-body">
                    {loadingFY ? (
                        <div className="loading-center" style={{ padding: "1rem" }}><div className="spinner" /></div>
                    ) : financialYearRows.length === 0 ? (
                        <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: "1rem" }}>No financial years yet.</p>
                    ) : (
                        <div style={{ marginBottom: "1.25rem" }}>
                            {financialYearRows.map((y) => (
                                <YearCard key={y._id} year={y} type="financial" isActive={y.isActive} onSetActive={handleSetActiveFY} onDelete={handleDeleteFY} />
                            ))}
                        </div>
                    )}
                    <div style={{ borderTop: "1px solid var(--border)", paddingTop: "1rem" }}>
                        <p style={{ fontWeight: 600, fontSize: 13, marginBottom: "0.75rem" }}>Create New Financial Year</p>
                        <form onSubmit={handleCreateFY}>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label className="form-label">Label *</label>
                                    <input className="form-control" placeholder="F.Y. 2026-2027" value={fyForm.label} onChange={(e) => setFyForm((p) => ({ ...p, label: e.target.value }))} required />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Assessment Year Label</label>
                                    <input className="form-control" placeholder="A.Y. 2027-2028" value={fyForm.assessmentYearLabel} onChange={(e) => setFyForm((p) => ({ ...p, assessmentYearLabel: e.target.value }))} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Start Date * (April 1)</label>
                                    <input type="date" className="form-control" value={fyForm.startDate} onChange={(e) => setFyForm((p) => ({ ...p, startDate: e.target.value }))} required />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">End Date * (March 31)</label>
                                    <input type="date" className="form-control" value={fyForm.endDate} onChange={(e) => setFyForm((p) => ({ ...p, endDate: e.target.value }))} required />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Opening Balance (₹)</label>
                                    <input type="number" className="form-control" placeholder="0" min="0" value={fyForm.openingBalance} onChange={(e) => setFyForm((p) => ({ ...p, openingBalance: e.target.value }))} />
                                </div>
                            </div>
                            <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: 13, marginTop: "0.5rem", cursor: "pointer" }}>
                                <input type="checkbox" checked={fyForm.setAsActive} onChange={(e) => setFyForm((p) => ({ ...p, setAsActive: e.target.checked }))} />
                                Set as active year immediately
                            </label>
                            <div style={{ marginTop: "0.75rem" }}>
                                <button type="submit" className="btn btn-primary" disabled={savingFY}>{savingFY ? "Creating…" : "Create Financial Year"}</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════════
          CARRY FORWARD
      ══════════════════════════════════════════════════════════════════════ */}
            <div className="card" style={{ marginBottom: "2rem" }}>
                <div className="card-header" style={{ borderLeft: "4px solid #16a34a", paddingLeft: "0.9rem" }}>
                    🔄 Start New Academic Year — Carry Forward Students
                </div>
                <div className="card-body">
                    <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: "1rem", lineHeight: 1.6 }}>
                        Eligible students (<strong>Active</strong>, <strong>Passed</strong>, <strong>ATKT</strong>, <strong>Detained</strong>)
                        are copied to the new year — for students linked to a Course, the next year is computed automatically
                        (or they graduate as <strong>Passed Out</strong> if this was their final year). <strong>Dropped</strong> and{" "}
                        <strong>Passed Out</strong> students are excluded.
                    </p>
                    <div className="form-grid" style={{ marginBottom: "0.75rem" }}>
                        <div className="form-group">
                            <label className="form-label">Source Year (closing year)</label>
                            <select className="form-control" value={cfSourceId} onChange={(e) => { setCfSourceId(e.target.value); setCfPreview(null); setCfResult(null); }}>
                                <option value="">Select year to close</option>
                                {academicYearRows.map((y) => <option key={y._id} value={y._id}>{y.label}</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Target Year (new year)</label>
                            <select className="form-control" value={cfTargetId} onChange={(e) => setCfTargetId(e.target.value)}>
                                <option value="">Select new year</option>
                                {academicYearRows.filter((y) => y._id !== cfSourceId).map((y) => <option key={y._id} value={y._id}>{y.label}</option>)}
                            </select>
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
                        <button className="btn btn-outline" onClick={handleCFPreview} disabled={!cfSourceId || loadingCFPreview}>
                            {loadingCFPreview ? "Loading…" : "Preview Carry Forward"}
                        </button>
                        <button className="btn btn-success" onClick={handleCFExecute} disabled={!cfSourceId || !cfTargetId || executingCF}>
                            {executingCF ? "Processing…" : "▶ Execute Carry Forward"}
                        </button>
                    </div>
                    {cfPreview && (
                        <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "1rem", marginBottom: cfResult ? "1rem" : 0 }}>
                            <p style={{ fontWeight: 600, fontSize: 13, marginBottom: "0.5rem" }}>Preview for "{cfPreview.sourceYear}"</p>
                            <div style={{ display: "flex", gap: "1.5rem", fontSize: 13, marginBottom: "0.75rem", flexWrap: "wrap" }}>
                                <span>Total: <strong>{cfPreview.totalStudents}</strong></span>
                                <span style={{ color: "#16a34a" }}>Will carry: <strong>{cfPreview.willCarry}</strong></span>
                                <span style={{ color: "#002747" }}>Will graduate: <strong>{cfPreview.willGraduate}</strong></span>
                                <span style={{ color: "#dc2626" }}>Will NOT carry: <strong>{cfPreview.willNotCarry}</strong></span>
                            </div>

                            {cfCarry.length > 0 && (
                                <div style={{ marginBottom: "0.75rem" }}>
                                    <p style={{ fontSize: 12, color: "#166534", fontWeight: 600, marginBottom: "0.3rem" }}>Carrying forward:</p>
                                    <div style={{ maxHeight: 220, overflowY: "auto" }}>
                                        {cfCarry.map((s) => (
                                            <div key={s._id} style={{ fontSize: 12, color: "#374151", display: "flex", justifyContent: "space-between", gap: "0.5rem", padding: "2px 0" }}>
                                                <span>{s.fullName} ({s.enrollmentNo}) — {s.status}</span>
                                                <span style={{ color: "#166534" }}>
                                                    → {s.computedTarget?.label}
                                                    {s.computedTarget?.mode === "legacy" && (
                                                        <span style={{ color: "#d97706" }} title="No Course linked — year not auto-computed"> (manual)</span>
                                                    )}
                                                    {s.backlogCount > 0 && (
                                                        <span style={{ color: "#b45309" }}> · {s.backlogCount} backlog</span>
                                                    )}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {cfGraduate.length > 0 && (
                                <div style={{ marginBottom: "0.75rem" }}>
                                    <p style={{ fontSize: 12, color: "#002747", fontWeight: 600, marginBottom: "0.3rem" }}>Graduating (Passed Out):</p>
                                    {cfGraduate.map((s) => (
                                        <div key={s._id} style={{ fontSize: 12, color: "#374151" }}>{s.fullName} ({s.enrollmentNo}) — {s.graduatingFrom}</div>
                                    ))}
                                </div>
                            )}

                            {cfNoCarry.length > 0 && (
                                <div>
                                    <p style={{ fontSize: 12, color: "#dc2626", fontWeight: 600, marginBottom: "0.3rem" }}>Not carrying forward:</p>
                                    {cfNoCarry.map((s) => (
                                        <div key={s._id} style={{ fontSize: 12, color: "#6b7280" }}>{s.fullName} ({s.enrollmentNo}) — {s.status}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    {cfResult && (
                        <div style={{ background: "#e8f1f8", border: "1px solid #b6d5ec", borderRadius: 8, padding: "1rem" }}>
                            <p style={{ fontWeight: 600, fontSize: 13, color: "#002747", marginBottom: "0.5rem" }}>✅ Carry Forward Complete</p>
                            <div style={{ display: "flex", gap: "1.5rem", fontSize: 13, flexWrap: "wrap" }}>
                                <span style={{ color: "#16a34a" }}>Created: <strong>{cfResult.totalCreated}</strong></span>
                                <span style={{ color: "#002747" }}>Graduated: <strong>{cfResult.totalGraduated}</strong></span>
                                <span style={{ color: "#d97706" }}>Skipped: <strong>{cfResult.totalSkipped}</strong></span>
                                {cfResult.totalErrors > 0 && <span style={{ color: "#dc2626" }}>Errors: <strong>{cfResult.totalErrors}</strong></span>}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════════
          BACKUP & EXPORT
      ══════════════════════════════════════════════════════════════════════ */}
            <div className="card" style={{ marginBottom: "2rem" }}>
                <div className="card-header" style={{ borderLeft: "4px solid #0891b2", paddingLeft: "0.9rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <FiPackage size={15} color="#0891b2" />
                        Backup & Export Data
                    </div>
                    <button
                        className="btn btn-outline btn-sm"
                        onClick={handleExportAll}
                        style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
                    >
                        <FiDownload size={12} /> Export All
                    </button>
                </div>
                <div className="card-body">
                    <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: "1.25rem", lineHeight: 1.6 }}>
                        Download any module's data as an Excel file (.xlsx) directly from your browser — no server ZIP required.
                        Academic-year-dependent exports use the <strong>currently selected academic year</strong> on the Dashboard.
                        {noAY && (
                            <span style={{ color: "#dc2626", marginLeft: 4, fontWeight: 600 }}>
                                ⚠ No academic year selected — some exports will be disabled.
                            </span>
                        )}
                    </p>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "0.9rem", alignItems: "stretch" }}>

                        <ExportCard
                            icon={FiUsers} color="#003b67"
                            title="Students Register"
                            description={noAY ? "Needs active academic year" : `All students — ${academicYear.label}`}
                            loading={exportLoading.students}
                            onExport={handleExportStudents}
                            disabled={noAY}
                            disabledReason="Select an academic year from the Dashboard first."
                        />

                        <ExportCard
                            icon={FiBriefcase} color="#059669"
                            title="Employees Register"
                            description="All employees — no year filter"
                            loading={exportLoading.employees}
                            onExport={handleExportEmployees}
                        />

                        <ExportCard
                            icon={FiCreditCard} color="#d97706"
                            title="Fee Receipts"
                            description={noAY ? "Needs active academic year" : `All receipts — ${academicYear.label}`}
                            loading={exportLoading.fees}
                            onExport={handleExportFees}
                            disabled={noAY}
                            disabledReason="Select an academic year from the Dashboard first."
                        />

                        <ExportCard
                            icon={FiAward} color="#7c3aed"
                            title="Bonafide Certificates"
                            description={noAY ? "Needs active academic year" : `All bonafide certs — ${academicYear.label}`}
                            loading={exportLoading.bonafide}
                            onExport={handleExportBonafide}
                            disabled={noAY}
                            disabledReason="Select an academic year from the Dashboard first."
                        />

                        <ExportCard
                            icon={FiFileText} color="#0891b2"
                            title="Leaving Certificates (TC)"
                            description="All leaving certificates — no year filter"
                            loading={exportLoading.leaving}
                            onExport={handleExportLeaving}
                        />

                        <ExportCard
                            icon={FiClipboard} color="#16a34a"
                            title="Student Attendance Summary"
                            description={noAY ? "Needs active academic year" : `Full-year summary — ${academicYear.label}`}
                            loading={exportLoading.attendance}
                            onExport={handleExportAttendance}
                            disabled={noAY}
                            disabledReason="Select an academic year from the Dashboard first."
                        />

                        <ExportCard
                            icon={FiBookOpen} color="#dc2626"
                            title="Exams & Results Summary"
                            description={noAY ? "Needs active academic year" : `All exams — ${academicYear.label}`}
                            loading={exportLoading.exams}
                            onExport={handleExportExams}
                            disabled={noAY}
                            disabledReason="Select an academic year from the Dashboard first."
                        />

                        <div style={{ border: "1px solid var(--border)", borderLeft: "4px solid #7c3aed", borderRadius: 8, padding: "1rem 1.1rem", display: "grid", gap: "0.75rem", height: "100%" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                    <div style={{ width: 30, height: 30, borderRadius: 8, background: "#7c3aed18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                        <FiDollarSign size={14} color="#7c3aed" />
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 13 }}>Salary / Payroll</div>
                                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Export salary records for a specific month</div>
                                    </div>
                                </div>
                                <button
                                    className="btn btn-outline btn-sm"
                                    onClick={handleExportSalary}
                                    disabled={exportLoading.salary}
                                    style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", flexShrink: 0 }}
                                >
                                    <FiDownload size={12} />
                                    {exportLoading.salary ? "Exporting…" : "Export"}
                                </button>
                            </div>

                            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                                <select
                                    className="form-control"
                                    style={{ width: 140, fontSize: 13 }}
                                    value={salaryExportMonth}
                                    onChange={(e) => setSalaryExportMonth(Number(e.target.value))}
                                >
                                    {MONTHS.slice(1).map((m, i) => (
                                        <option key={i + 1} value={i + 1}>{m}</option>
                                    ))}
                                </select>
                                <select
                                    className="form-control"
                                    style={{ width: 110, fontSize: 13 }}
                                    value={salaryExportYear}
                                    onChange={(e) => setSalaryExportYear(Number(e.target.value))}
                                >
                                    {yearOpts.map((y) => <option key={y} value={y}>{y}</option>)}
                                </select>
                                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                    → {MONTHS[salaryExportMonth]} {salaryExportYear}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}